<template>
  <client-only>
    <component
      :is="dialogComponent"
      v-model="loginDialog"
      :content-class="`auth-bottom-sheet__${sheetPosition} ma-0 !wd-max-h-full <xs:wd-h-full`"
      :max-width="isMobile ? '100%' : 440"
      persistent
      eager
    >
      <v-card
        class="auth-dialog-container wd-mx-auto wd-w-full sm:(wd-min-w-110)"
        :class="{ mobile: isMobile, '!wd-pb-10': !confirmStep && !isMobile }"
      >
        <div class="btn-block text-right mb-2">
          <v-btn v-if="canClose" class="close-btn" :min-width="24" :width="24" :height="24" variant="text" @click="onClose">
            <v-icon :size="20"> mdi-close </v-icon>
          </v-btn>
          <div v-else class="wd-h-1" />
        </div>

        <div v-show="isTitleBlockShown">
          <div class="login-dalog-title text-center wd-mb-6">
            <v-img
              class="logo__img mx-auto"
              :class="modalIntroBrand ? 'wd-mb-3' : 'wd-mb-3'"
              max-width="165"
              cover
              src="@/assets/images/logo/logo.svg?url"
            />
            <div class="card-header">
              <p class="text-h6 mb-0">{{ headerTitle }}</p>
            </div>
          </div>

          <auth-index
            v-show="authStep === 'index'"
            ref="authIndex"
            :modal-intro-brand="modalIntroBrand"
            @success="successEvent"
            @change-auth-step="changeAuthStep"
            @show-consent-step="handleShowConsentStep"
          />

          <register v-if="authStep === 'sign-up'" ref="registerComponent" @success="successEvent" @change-auth-step="changeAuthStep" />

          <login v-else-if="authStep === 'login'" @success="successEvent" @change-auth-step="changeAuthStep" />

          <forgot-password v-else-if="authStep === 'forgot-password'" @change-auth-step="changeAuthStep" />
        </div>

        <consent-step v-if="confirmStep === 'consent'" :loading="isConfirming" @confirm="handleConsentConfirm" />

        <confirm-discard v-else-if="confirmStep === 'confirm-discard'" @confirm="handleDiscardConfirm" @cancel="handleDiscardCancel" />
      </v-card>
    </component>
  </client-only>
</template>

<script>
import { mapGetters } from 'vuex'
import { useEventBus } from '@vueuse/core'
import tools from '@/mixin/tools'

import AuthIndex from '@/components/dialog/auth/IndexTenants/FunNow/index.vue'
import Register from '@/components/dialog/auth/register.vue'
import Login from '@/components/dialog/auth/login.vue'
import ForgotPassword from '@/components/dialog/auth/forgot-password.vue'
import ConsentStep from '@/components/dialog/auth/consent-step.vue'
import ConfirmDiscard from '@/components/dialog/auth/confirm-discard.vue'
import { LoginEvent } from '@/view-models/event-bus'

export default defineComponent({
  name: 'FunNowAuth',
  components: {
    AuthIndex,
    Register,
    Login,
    ForgotPassword,
    ConsentStep,
    ConfirmDiscard,
  },
  mixins: [tools],
  setup() {
    const loginBus = useEventBus(LoginEvent)

    const { dialogComponent } = useBottomSheetComponent()

    return {
      loginBus,
      dialogComponent,
    }
  },
  data() {
    return {
      authStep: 'index',
      confirmStep: '',
      errorMessage: '',
      isMobile: false,
      isConfirming: false,
      pendingConsentCallback: null,
      previousAuthStep: '',
      previousConfirmStep: '',
    }
  },
  computed: {
    isTitleBlockShown() {
      return !this.confirmStep
    },
    sheetPosition() {
      return this.isMobile ? 'default' : 'center'
    },
    canClose() {
      if (this.isConfirming) return false
      return this.loginDialogOptions.canClose ?? true
    },
    modalIntroBrand() {
      return this.loginDialogOptions.modalIntroBrand
    },
    headerTitle() {
      let title = this.$t('brand_description')
      switch (this.authStep) {
        case 'sign-up':
          title = this.$t('all_register')
          break
        case 'login':
          title = this.$t('all_login')
          break
        case 'forgot-password':
          title = this.$t('forgot_pwd_title')
          break
        case 'index':
        default:
          title = this.$t('brand_description')
          break
      }
      return title
    },
    loginDialog: {
      get() {
        return this.$store.state.dialog.loginDialog
      },
      set(val) {
        this.changeDialogShow(val)
      },
    },
    ...mapGetters({
      loginDialogOptions: 'dialog/loginDialogOptions',
      user: 'authentication/getUser',
    }),
  },
  watch: {
    loginDialog(val) {
      if (val) {
        this.addResizeListener()
        this.setDefaultData()
      } else {
        this.removeResizeListener()
      }
    },
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      this.removeResizeListener()
    }
  },
  mounted() {
    this.onResize()
    this.addResizeListener()
  },
  methods: {
    addResizeListener() {
      window.addEventListener('resize', this.onResize, { passive: true })
    },
    removeResizeListener() {
      window.removeEventListener('resize', this.onResize, { passive: true })
    },
    onResize() {
      this.isMobile = window.innerWidth < 600
    },
    onClose() {
      // sign-up: 只有在表單 dirty 時才確認是否要放棄註冊
      if (this.authStep === 'sign-up') {
        if (this.confirmStep === 'confirm-discard') {
          this.handleDiscardCancel()
          return
        }
        const registerForm = this.$refs.registerComponent?.$refs.registerForm
        if (registerForm?.meta?.dirty) {
          this.previousAuthStep = this.authStep
          this.confirmStep = 'confirm-discard'
          return
        }
      } else if (this.confirmStep === 'consent') {
        this.previousAuthStep = this.authStep
        this.previousConfirmStep = this.confirmStep
        this.confirmStep = 'confirm-discard'
        return
      }
      this.confirmStep = ''
      this.changeDialogShow(false)
    },
    setDefaultData() {
      this.authStep = 'index'
    },
    changeAuthStep(step) {
      this.authStep = step
    },
    successEvent() {
      if (this.loginDialogOptions.hasOwnProperty('success')) {
        this.loginDialogOptions.success()
      }
      this.loginBus.emit(true, this.loginDialogOptions.from)
      this.$store.dispatch('dialog/setLoginDialog', { state: false })
    },
    changeDialogShow(state) {
      if (!state && !this.user) {
        this.loginBus.emit(false, this.loginDialogOptions.from)
      }
      this.$store.dispatch('dialog/setLoginDialog', { state })
    },
    handleShowConsentStep(callback) {
      // For third-party login, it may directly go to the consent step without opening the dialog, so we need to ensure that the dialog is open here.
      this.loginDialog = true
      this.pendingConsentCallback = callback
      this.confirmStep = 'consent'
    },
    async handleConsentConfirm(userContract) {
      if (this.pendingConsentCallback) {
        this.isConfirming = true
        try {
          await this.pendingConsentCallback(userContract)
          this.pendingConsentCallback = null
          this.confirmStep = ''
          this.loginDialog = false
        } finally {
          this.isConfirming = false
        }
      }
    },
    handleDiscardConfirm() {
      this.$refs.authIndex?.resetConsent?.()
      // 清理狀態
      this.pendingConsentCallback = null
      this.confirmStep = ''
      this.previousConfirmStep = ''

      this.loginDialog = false
    },
    handleDiscardCancel() {
      // 回到原本的 authStep
      this.authStep = this.previousAuthStep
      this.confirmStep = this.previousConfirmStep
    },
  },
})
</script>

<style scoped lang="scss">
.auth-dialog-container {
  padding: 28px 40px 20px;
  border-radius: 4px !important;

  .close-btn {
    margin-right: -12px;
  }

  &.mobile {
    border-radius: 0 !important;
  }
}
</style>
<style lang="scss">
.auth-bottom-sheet {
  &__center {
    align-self: center !important;
  }
}

.dna-icon {
  margin-top: 2px;
  cursor: pointer;
}
</style>
